import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    Sun, Moon, LogOut, ChevronsLeft, ChevronsRight, Search,
    LayoutGrid, Users, Settings2, Sparkles, Wallet, Package, Activity, ClipboardCheck,
    Plus, MessageSquare, MapPin, Bell, Repeat,
} from "lucide-react";
import { useAuth, isSuporteRole } from "@/lib/AuthContext";
import CmdK from "./CmdK";
import SyncStatus from "./SyncStatus";
import Fab from "./Fab";
import KommoCircuitBanner from "./KommoCircuitBanner";

const NAV = [
    { to: "/hoje", label: "Hoje", icon: LayoutGrid, testid: "nav-hoje" },
    { to: "/producao", label: "Produção", icon: ClipboardCheck, testid: "nav-producao" },
    { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clientes" },
    { to: "/instalacoes-v2", label: "Instalações", icon: Package, testid: "nav-instalacoes" },
    { to: "/financeiro", label: "Financeiro", icon: Wallet, testid: "nav-financeiro", adminOnly: true },
    { to: "/estado-sistema", label: "Estado", icon: Activity, testid: "nav-estado", adminOnly: true },
    { to: "/configuracoes", label: "Configurações", icon: Settings2, testid: "nav-config", external: true, adminOnly: true },
    // Suporte: páginas que só existem no layout antigo abrem DENTRO do Shell v2 (mesmas permissões)
    { to: "/nova-venda", label: "Nova venda", icon: Plus, testid: "nav-nova-venda", suporte: true },
    { to: "/conversas", label: "Fecho do dia", icon: MessageSquare, testid: "nav-fecho-dia", suporte: true },
    { to: "/localizacao", label: "Mapa", icon: MapPin, testid: "nav-mapa", suporte: true },
    { to: "/alertas", label: "Alertas", icon: Bell, testid: "nav-alertas", suporte: true },
];

const POSVENDA_OCULTOS = ["/nova-venda", "/conversas", "/producao"];
const navFor = (user) => NAV.filter((it) => (!it.adminOnly || user?.role === "admin") && (!it.suporte || isSuporteRole(user?.role)) && !(user?.perfil === "posvenda" && POSVENDA_OCULTOS.includes(it.to)));

export default function ShellV2({ children }) {
    const { user, logout, switchRole } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("v2-sidebar-collapsed") === "1"; } catch { return false; }
    });
    const [dark, setDark] = useState(() => {
        try { return localStorage.getItem("v2-theme") === "dark"; } catch { return false; }
    });
    const [cmdkOpen, setCmdkOpen] = useState(false);
    const navItems = navFor(user);

    useEffect(() => {
        if (dark) document.documentElement.classList.add("dark");
        else document.documentElement.classList.remove("dark");
        try { localStorage.setItem("v2-theme", dark ? "dark" : "light"); } catch {}
    }, [dark]);

    useEffect(() => {
        try { localStorage.setItem("v2-sidebar-collapsed", collapsed ? "1" : "0"); } catch {}
    }, [collapsed]);

    // Cmd/Ctrl+K opens command bar
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdkOpen((s) => !s);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div
            className="min-h-screen font-body flex"
            style={{ background: "var(--v2-background)", color: "var(--v2-foreground)" }}
        >
            {/* Sidebar */}
            <aside
                className={`hidden md:flex flex-col border-r transition-[width] duration-200 ${collapsed ? "w-16" : "w-60"}`}
                style={{ borderColor: "var(--v2-border)", background: "var(--v2-surface)" }}
                data-testid="v2-sidebar"
            >
                <div className="h-12 flex items-center px-3 border-b" style={{ borderColor: "var(--v2-border)" }}>
                    <Sparkles size={16} style={{ color: "var(--v2-primary)" }} />
                    {!collapsed && (
                        <span className="ml-2 font-headings font-bold tracking-tight text-sm">Salvador MEO</span>
                    )}
                </div>
                <nav className="flex-1 py-3 px-2 space-y-0.5">
                    {navItems.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `group flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] font-medium transition-colors ${
                                    isActive ? "text-[color:var(--v2-foreground)]" : "text-[color:var(--v2-muted)] hover:text-[color:var(--v2-foreground)]"
                                }`
                            }
                            style={({ isActive }) => (isActive ? { background: "var(--v2-surface-hover)" } : {})}
                        >
                            <Icon size={15} className="shrink-0" />
                            {!collapsed && <span className="truncate">{label}</span>}
                        </NavLink>
                    ))}
                </nav>

                {/* Bottom */}
                <div className="border-t p-2 space-y-1" style={{ borderColor: "var(--v2-border)" }}>
                    <button
                        onClick={() => setDark((v) => !v)}
                        className="w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] font-medium hover:bg-[color:var(--v2-surface-hover)] transition-colors"
                        style={{ color: "var(--v2-muted)" }}
                        data-testid="v2-theme-toggle"
                    >
                        {dark ? <Sun size={15} /> : <Moon size={15} />}
                        {!collapsed && <span>{dark ? "Modo claro" : "Modo escuro"}</span>}
                    </button>
                    {user?.realRole === "admin" && user?.role !== "admin" && (
                        <button
                            onClick={() => switchRole("admin")}
                            className="w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] font-medium hover:bg-[color:var(--v2-surface-hover)] transition-colors"
                            style={{ color: "var(--v2-primary)" }}
                            data-testid="v2-switch-role-back"
                        >
                            <Repeat size={15} />
                            {!collapsed && <span>Voltar a Admin</span>}
                        </button>
                    )}
                    <button
                        onClick={() => logout && logout()}
                        className="w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] font-medium hover:bg-[color:var(--v2-surface-hover)] transition-colors"
                        style={{ color: "var(--v2-muted)" }}
                        data-testid="v2-logout"
                    >
                        <LogOut size={15} />
                        {!collapsed && <span>Sair</span>}
                    </button>
                    <button
                        onClick={() => setCollapsed((v) => !v)}
                        className="w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] font-medium hover:bg-[color:var(--v2-surface-hover)] transition-colors"
                        style={{ color: "var(--v2-muted)" }}
                        data-testid="v2-sidebar-toggle"
                    >
                        {collapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
                        {!collapsed && <span className="truncate">Recolher</span>}
                    </button>
                    {!collapsed && user && (
                        <div className="pt-2 mt-2 border-t px-2.5" style={{ borderColor: "var(--v2-border)" }}>
                            <div className="text-[11px] font-medium truncate" style={{ color: "var(--v2-foreground)" }}>
                                {user.name || user.email}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--v2-muted)" }}>
                                {user.role}
                            </div>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main area */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header
                    className="h-12 border-b flex items-center px-3 gap-3 shrink-0"
                    style={{ borderColor: "var(--v2-border)", background: "var(--v2-surface)" }}
                    data-testid="v2-topbar"
                >
                    {/* Mobile nav */}
                    <div className="md:hidden flex items-center gap-1 mr-1 overflow-x-auto max-w-[55vw]" data-testid="v2-mobile-nav">
                        {navItems.map(({ to, icon: Icon, testid, label }) => (
                            <NavLink
                                key={to}
                                to={to}
                                data-testid={`mobile-${testid}`}
                                title={label}
                                className={({ isActive }) =>
                                    `flex items-center justify-center h-8 w-8 shrink-0 rounded-md ${
                                        isActive ? "text-[color:var(--v2-foreground)] bg-[color:var(--v2-surface-hover)]" : "text-[color:var(--v2-muted)]"
                                    }`
                                }
                            >
                                <Icon size={15} />
                            </NavLink>
                        ))}
                    </div>

                    {/* Cmd+K trigger (center) */}
                    <button
                        onClick={() => setCmdkOpen(true)}
                        className="flex-1 max-w-md mx-auto flex items-center gap-2 h-8 px-3 rounded-md border text-[13px] transition-colors hover:bg-[color:var(--v2-surface-hover)]"
                        style={{ borderColor: "var(--v2-border)", color: "var(--v2-muted)" }}
                        data-testid="v2-cmdk-trigger"
                    >
                        <Search size={14} />
                        <span className="flex-1 text-left">Procurar clientes, ações…</span>
                        <kbd
                            className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded border"
                            style={{ borderColor: "var(--v2-border)" }}
                        >
                            ⌘K
                        </kbd>
                    </button>

                    <div className="ml-auto flex items-center gap-2">
                        <SyncStatus />
                    </div>
                </header>

                {/* Kommo Circuit Breaker banner — só aparece quando WAF ativo */}
                <KommoCircuitBanner />

                {/* Content */}
                <main className="flex-1 overflow-y-auto" data-testid="v2-content">
                    {children}
                </main>
            </div>

            <CmdK open={cmdkOpen} onOpenChange={setCmdkOpen} onNavigate={(to) => { setCmdkOpen(false); navigate(to); }} />
            <Fab />
        </div>
    );
}
